import { useState } from "react";
import globalStyles from "../../../../assets/styles/base/globalPage.module.scss";
import { useGetPrinters } from "../../../../hooks/api/queries/usePrint";
import primaryComponents from "../../../primaryComponents";
import styles from "./styles.module.scss";
import {
  AppleIcon,
  ArrowCircle,
  DownloadIcon,
  MicrosoftIcon,
  PrinterIcon,
} from "../../../../assets/svgIcons";
import { LoveIcon } from "../../../../assets/svgIcons/LoveIcon";
import { TestView } from "../../../printViews/TestView";

const PrinterConfigForm = () => {
  const [allowPrint, setAllowPrint] = useState(false);
  const [defaultPaper] = useState(
    localStorage.getItem("default-LB-printer-paper")
      ? JSON.parse(localStorage.getItem("default-LB-printer-paper"))
      : null,
  );
  const [defaultPrinter] = useState(
    localStorage.getItem("default-LB-printer")
      ? JSON.parse(localStorage.getItem("default-LB-printer"))
      : null,
  );
  const [useConfig, setConfig] = useState(
    localStorage.getItem("use-LB-printer-config")
      ? JSON.parse(localStorage.getItem("use-LB-printer-config"))
      : true,
  );
  const [defaultLabelPrinter] = useState(
    localStorage.getItem("default-LB-label-printer")
      ? JSON.parse(localStorage.getItem("default-LB-label-printer"))
      : null,
  );
  const [useLabelConfig, setLabelConfig] = useState(
    localStorage.getItem("use-LB-label-printer-config")
      ? JSON.parse(localStorage.getItem("use-LB-label-printer-config"))
      : false,
  );
  const { data: printers } = useGetPrinters({ isEnabled: true });
  const paperOptions = [
    { name: "50mm", id: "384" },
    { name: "80mm", id: "576" },
  ];

  const updatePrinterInfo = (id) => {
    const selectedPrinter = printers?.find((printer) => printer?.id === id);
    if (selectedPrinter)
      localStorage.setItem(
        "default-LB-printer",
        JSON.stringify(selectedPrinter),
      );
  };

  const updatePaperSize = (id) => {
    const paper = paperOptions?.find((paper) => paper?.id === id);
    if (paper)
      localStorage.setItem("default-LB-printer-paper", JSON.stringify(paper));
  };

  const updateLabelPrinterInfo = (id) => {
    const selectedPrinter = printers?.find((printer) => printer?.id === id);
    if (selectedPrinter)
      localStorage.setItem(
        "default-LB-label-printer",
        JSON.stringify(selectedPrinter),
      );
  };

  const openDownloadLink = (link) => {
    window.open(link);
  };

  return (
    <>
      <div
        className={`${globalStyles.cardWithBackground} ${styles.printerConfig} white p-4`}
        style={{ gap: "15px" }}
      >
        <div
          className={`${styles.printerConfig__header} ${globalStyles.pageHeaders}`}
        >
          <div style={{ maxWidth: "600px" }}>
            <h4>Configuration Steps</h4>
            <p>
              Follow these easy steps below to configure your printer on this
              device for better experience without browser pop-ups
            </p>
          </div>

          <div className="d-flex flex-column" style={{ gap: "5px" }}>
            <PrinterIcon width={40} height={40} className="mx-auto" />
            <span>
              Turn Off/On{" "}
              <primaryComponents.Switch
                name={"printConfigSwitch"}
                id="printConfigSwitch"
                isChecked={useConfig}
                onChange={() => {
                  localStorage.setItem("use-LB-printer-config", !useConfig);
                  setConfig((prev) => !prev);
                }}
              />
            </span>
          </div>
        </div>

        <div className="d-flex flex-column" style={{ gap: "50px" }}>
          <div className={`${styles.section} ${styles.largeView}`}>
            <span className={styles.section__number}>
              <span className="success--text">Step 1</span> of 5
            </span>
            <hr className={styles.section__line} />
            <div className={styles.section__content}>
              <h6 className="mb-0">
                Connect your printer to this device with a USB
              </h6>
            </div>
          </div>

          <div className={`${styles.section} ${styles.largeView}`}>
            <span className={styles.section__number}>
              <span className="success--text">Step 2</span> of 5
            </span>
            <hr className={styles.section__line} />
            <div className={styles.section__content}>
              <h6 className="mb-0">
                Download and install the print agent for your Operating System
              </h6>

              <div className="d-flex flex-wrap" style={{ gap: "30px" }}>
                <div
                  className={`${globalStyles.cardWithBorder} ${styles.osCard}`}
                >
                  <div className="text-center">
                    <MicrosoftIcon />
                    <p>
                      Print Agent for{" "}
                      <span className="font-weight-bold dark--text">
                        Windows
                      </span>{" "}
                      Operating System{" "}
                    </p>
                  </div>
                  <primaryComponents.Button
                    classNames={"btn btn--primary"}
                    style={{ height: "40px" }}
                    onClick={() => {
                      openDownloadLink(
                        process.env
                          .REACT_APP_LUMI_PRINT_AGENT_WINDOWS_DOWNLOAD_URL,
                      );
                    }}
                  >
                    <DownloadIcon className="mr-2" />
                    Download
                  </primaryComponents.Button>

                  <div
                    className={`${globalStyles.cardWithBackground} mt-3 card-plain`}
                    style={{ gap: "20px" }}
                  >
                    <p className="error--text text-center small-text-size">
                      Windows OS Installation steps:
                    </p>
                    <div className={styles.section}>
                      <span className={`${styles.section__number} white`}>
                        <span className="success--text">Step 1</span> of 4
                      </span>
                      <hr
                        className={`${styles.section__line}  ${styles.small}`}
                      />
                      <div className={styles.section__content}>
                        <p className="small-text-size">
                          Extract the downloaded zip file
                        </p>
                      </div>
                    </div>
                    <div className={styles.section}>
                      <span
                        className={`${styles.section__number} ${styles.small} white`}
                      >
                        <span className="success--text">Step 2</span> of 4
                      </span>
                      <hr
                        className={`${styles.section__line}  ${styles.small}`}
                      />
                      <div className={styles.section__content}>
                        <p className="small-text-size">
                          Right-click the Start button and open{" "}
                          <span className="dark--text font-weight-bold">
                            “Windows PowerShell (Admin)”
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className={styles.section}>
                      <span
                        className={`${styles.section__number} ${styles.small} white`}
                      >
                        <span className="success--text">Step 3</span> of 4
                      </span>
                      <hr
                        className={`${styles.section__line}  ${styles.small}`}
                      />
                      <div className={styles.section__content}>
                        <p className="small-text-size">
                          In File Explorer, open the extracted folder and click
                          once in the address bar to copy its path. Then in
                          PowerShell, type{" "}
                          <span className="dark--text font-weight-bold">
                            cd &quot;
                          </span>
                          , paste the path (right-click, or Ctrl+V), add a
                          closing{" "}
                          <span className="dark--text font-weight-bold">
                            &quot;
                          </span>{" "}
                          and press Enter
                        </p>
                      </div>
                    </div>
                    <div className={styles.section}>
                      <span
                        className={`${styles.section__number} ${styles.small} white`}
                      >
                        <span className="success--text">Step 4</span> of 4
                      </span>
                      <hr
                        className={`${styles.section__line}  ${styles.small}`}
                      />
                      <div className={styles.section__content}>
                        <p className="small-text-size">
                          Run: <br />
                          <span className="dark--text font-weight-bold">
                            powershell -ExecutionPolicy Bypass -File
                            .\installer\install.ps1
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className={`${globalStyles.cardWithBorder} ${styles.osCard}`}
                >
                  <div className="text-center">
                    <AppleIcon />
                    <p>
                      Driver for{" "}
                      <span className="font-weight-bold dark--text">Mac</span>{" "}
                      Operating System{" "}
                    </p>
                  </div>
                  <primaryComponents.Button
                    classNames={"btn btn--primary"}
                    style={{ height: "40px" }}
                    onClick={() => {
                      openDownloadLink(
                        process.env
                          .REACT_APP_LUMI_PRINT_DRIVER_MAC_DOWNLOAD_URL,
                      );
                    }}
                  >
                    <DownloadIcon className="mr-2" />
                    Download
                  </primaryComponents.Button>

                  <div
                    className={`${globalStyles.cardWithBackground} mt-3 card-plain`}
                    style={{ gap: "20px" }}
                  >
                    <p className="error--text text-center small-text-size">
                      Mac OS Driver Installation steps:
                    </p>
                    <div className={styles.section}>
                      <span className={`${styles.section__number} white`}>
                        <span className="success--text">Step 1</span> of 2
                      </span>
                      <hr
                        className={`${styles.section__line}  ${styles.small}`}
                      />
                      <div className={styles.section__content}>
                        <p className="small-text-size">
                          Open the downloaded file
                        </p>
                      </div>
                    </div>
                    <div className={styles.section}>
                      <span
                        className={`${styles.section__number} ${styles.small} white`}
                      >
                        <span className="success--text">Step 1</span> of 2
                      </span>
                      <hr
                        className={`${styles.section__line}  ${styles.small}`}
                      />
                      <div className={styles.section__content}>
                        <p className="small-text-size">
                          Double click on this driver: <br />
                          <span className="dark--text font-weight-bold">
                            “Lumi-print-driver-mac
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={`${styles.section} ${styles.largeView}`}>
            <span className={styles.section__number}>
              <span className="success--text">Step 3</span> of 5
            </span>
            <hr className={styles.section__line} />
            <div className={styles.section__content}>
              <h6 className="mb-0">Select your printer from the list below</h6>
              <div style={{ width: "100%", maxWidth: "500px" }}>
                <primaryComponents.SelectField
                  options={printers || []}
                  idKey={"id"}
                  nameKey={"name"}
                  placeholder={"Select a printer"}
                  value={defaultPrinter?.name || "Select a printer"}
                  onChange={(value) => {
                    updatePrinterInfo(value);
                  }}
                />
              </div>
            </div>
          </div>

          <div className={`${styles.section} ${styles.largeView}`}>
            <span className={styles.section__number}>
              <span className="success--text">Step 4</span> of 5
            </span>
            <hr className={styles.section__line} />
            <div className={styles.section__content}>
              <h6 className="mb-0">Select your paper size below</h6>
              <div style={{ width: "100%", maxWidth: "500px" }}>
                <primaryComponents.SelectField
                  options={paperOptions}
                  idKey={"id"}
                  nameKey={"name"}
                  placeholder={"Select a paper size"}
                  value={defaultPaper?.name || "Select a paper size"}
                  onChange={(value) => {
                    updatePaperSize(value);
                  }}
                  floatError={true}
                />
              </div>
            </div>
          </div>

          <div className={`${styles.section} ${styles.largeView}`}>
            <span className={styles.section__number}>
              <span className="success--text">Step 5</span> of 5
            </span>
            <hr className={styles.section__line} />
            <div className={styles.section__content}>
              <h6 className="d-flex align-items-center mb-0">
                Congratulations on getting this far! We appreciate your patience{" "}
                <LoveIcon />
              </h6>
              <div
                className="d-flex flex-wrap justify-content-between"
                style={{ gap: "15px" }}
              >
                <p style={{ maxWidth: "550px" }}>
                  Click the button on the far right to test if your
                  configuration is successful for both Windows & Mac operating
                  systems
                </p>
                <primaryComponents.Button
                  classNames={"btn btn--primary"}
                  style={{ height: "40px", width: "170px" }}
                  onClick={() => setAllowPrint(true)}
                >
                  <ArrowCircle className="mr-2" />
                  Test Print
                </primaryComponents.Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`${globalStyles.cardWithBackground} ${styles.printerConfig} white p-4 mt-4`}
        style={{ gap: "15px" }}
      >
        <div
          className={`${styles.printerConfig__header} ${globalStyles.pageHeaders}`}
        >
          <div style={{ maxWidth: "600px" }}>
            <h4>Label Printer (Optional)</h4>
            <p>
              If you print barcode labels on a separate label printer, select it
              below to print directly with no browser print dialog. Leave this
              off to keep printing labels the way you do today.
            </p>
          </div>

          <div className="d-flex flex-column" style={{ gap: "5px" }}>
            <PrinterIcon width={40} height={40} className="mx-auto" />
            <span>
              Turn Off/On{" "}
              <primaryComponents.Switch
                name={"labelPrintConfigSwitch"}
                id="labelPrintConfigSwitch"
                isChecked={useLabelConfig}
                onChange={() => {
                  localStorage.setItem(
                    "use-LB-label-printer-config",
                    !useLabelConfig,
                  );
                  setLabelConfig((prev) => !prev);
                }}
              />
            </span>
          </div>
        </div>

        <div style={{ width: "100%", maxWidth: "500px" }}>
          <primaryComponents.SelectField
            options={printers || []}
            idKey={"id"}
            nameKey={"name"}
            placeholder={"Select a label printer"}
            value={defaultLabelPrinter?.name || "Select a label printer"}
            onChange={(value) => {
              updateLabelPrinterInfo(value);
            }}
          />
        </div>
      </div>

      {allowPrint ? (
        <TestView
          allowPrint={allowPrint}
          closePrint={() => setAllowPrint(false)}
        />
      ) : null}
    </>
  );
};
export default PrinterConfigForm;
